import { Component, FC, ReactNode } from 'react';
import { createRefetchContainer, graphql, RelayRefetchProp } from 'react-relay';
import { connect } from 'react-redux';
import { getBuyerId } from 'dibs-cookie-jar';
import { GridRow, GridCol } from 'dibs-elements/exports/Grid';
import LinkColumn from './LinkColumn/LinkColumn';
import LinkColumnItem from './LinkColumn/LinkColumnItem';
import { FooterBottomRow } from './FooterBottomRow';
import { FooterLink } from './FooterLinkTypes';

import { linkColumnConfig, filterLinks } from '../footerHelpers';
import style from './Footer-style.scss';
import { Footer_viewer } from './__generated__/Footer_viewer.graphql';

export type enablerKeys = keyof Footer_viewer;

// to pad one column width of space on both edge of the footer
const PaddingGridCol: FC = () => <GridCol desktop={1} />;

type Props = {
    relay: RelayRefetchProp;
    updateUserState: boolean;
    hideFooter: boolean;
    viewer: Footer_viewer;
};

type State = {
    isClient: boolean;
};

class Footer extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { isClient: false };
        this.updateUserState = this.updateUserState.bind(this);
        this.mapListItems = this.mapListItems.bind(this);
    }

    componentDidMount(): void {
        // eslint-disable-next-line react/no-did-mount-set-state
        this.setState(prevState => {
            return { ...prevState, isClient: true };
        }, this.updateUserState);
    }

    componentDidUpdate(prevProps: Props): void {
        if (this.props.updateUserState && !prevProps.updateUserState) {
            this.updateUserState();
        }
    }

    updateUserState(): void {
        const userId = getBuyerId(document.cookie) || '';
        const hasUserId = !!userId;
        const refetchVariables = (): { userId: string; hasUserId: boolean } => ({
            userId: userId,
            hasUserId,
        });

        this.props.relay.refetch(refetchVariables);
    }

    mapListItems({ CustomComponent, ...item }: FooterLink): ReactNode {
        const { viewer } = this.props;
        const { isClient } = this.state;
        return CustomComponent ? (
            <CustomComponent key={item.dataTn} {...item} viewer={viewer} isClient={isClient} />
        ) : (
            <LinkColumnItem key={item.dataTn} {...item} />
        );
    }

    render(): ReactNode {
        const { isClient } = this.state;
        const { viewer, hideFooter } = this.props;

        return (
            <footer className={style.footer} hidden={hideFooter}>
                <>
                    <div className={style.footerRow}>
                        <GridRow>
                            <PaddingGridCol />
                            {linkColumnConfig.map(
                                (
                                    {
                                        enablerKey,
                                        disablerKey,
                                        columnWidth,
                                        title,
                                        items,
                                        additionalLink,
                                    },
                                    index
                                ) => {
                                    let showLinkColumn = true;
                                    if (enablerKey) {
                                        showLinkColumn = viewer[enablerKey] === true;
                                    }
                                    if (disablerKey) {
                                        showLinkColumn = viewer[disablerKey] === false;
                                    }
                                    return (
                                        showLinkColumn && (
                                            <GridCol
                                                key={`link-column-${index}`}
                                                desktop={columnWidth}
                                            >
                                                <LinkColumn title={title}>
                                                    {filterLinks(items, viewer).map(
                                                        this.mapListItems
                                                    )}
                                                </LinkColumn>
                                                {additionalLink && (
                                                    <div className={style.additionalLinkContainer}>
                                                        <LinkColumn title={additionalLink.title}>
                                                            {filterLinks(
                                                                additionalLink.items,
                                                                viewer
                                                            ).map(this.mapListItems)}
                                                        </LinkColumn>
                                                    </div>
                                                )}
                                            </GridCol>
                                        )
                                    );
                                }
                            )}
                            <PaddingGridCol />
                        </GridRow>
                    </div>
                    <FooterBottomRow
                        filterLinks={(links: FooterLink[]) => filterLinks(links, viewer)}
                        viewer={viewer}
                        isClient={isClient}
                    />
                </>
            </footer>
        );
    }
}

const mapStateToProps = (state: {
    header: {
        updateUserState: boolean;
        hideFooter: boolean;
    };
}): {
    updateUserState: boolean;
    hideFooter: boolean;
} => {
    const updateUserState = state?.header?.updateUserState;
    return {
        updateUserState,
        hideFooter: state?.header?.hideFooter,
    };
};

export default createRefetchContainer(
    connect(mapStateToProps)(Footer),
    {
        viewer: graphql`
            fragment Footer_viewer on Viewer {
                isTradeExperienceEnabled: isEnabledByLocale(featureName: "TRADE_EXPERIENCE")
                isSellerApplicationEnabled: isEnabledByLocale(featureName: "SELLER_APPLICATION")
                isAboutUsPageEnabled: isEnabledByLocale(featureName: "ABOUT_US_PAGE")
                isCareersPageEnabled: isEnabledByLocale(featureName: "CAREERS_PAGE")
                isPressPageEnabled: isEnabledByLocale(featureName: "PRESS_PAGE")
                isQuestionsPageEnabled: isEnabledByLocale(featureName: "QUESTIONS_PAGE")
                isSiteMapsEnabled: isEnabledByLocale(featureName: "HTML_SITE_MAPS")
                disableFooterSubscribe: isEnabled(key: "disableFooterSubscribe")
                ...CurrencySelector_viewer
                ...FooterBottomRow_viewer
                ...ConnectRow_viewer
                ...FooterSubscribe_viewer
            }
        `,
    },
    graphql`
        query FooterRefetchQuery($userId: String!, $hasUserId: Boolean!) {
            viewer {
                ...Footer_viewer
            }
        }
    `
);
